import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Snowflake, 
  ArrowLeft, 
  Music, 
  Calendar,
  Clock,
  MapPin,
  Mic2,
  PartyPopper,
  CalendarPlus,
  Download
} from "lucide-react";

// ====== ICS Generation Helpers ======
const pad = (n) => String(n).padStart(2, "0");
const fmtICS = (date) =>
  `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}00`;

// Parse "DD.MM.YYYY" + "HH:MM - HH:MM Uhr" / "HH:MM Uhr" → {start: Date, end: Date}
const parseEventDateTime = (dateStr, timeStr) => {
  const [d, m, y] = dateStr.split(".").map(Number);
  const cleaned = timeStr.replace(/\s*Uhr/i, "").trim();
  const parts = cleaned.split("-").map((s) => s.trim());
  const parseTime = (t) => t.split(":").map(Number);
  const [sh, sm] = parseTime(parts[0]);
  const start = new Date(y, m - 1, d, sh, sm || 0);
  let end;
  if (parts[1]) {
    const [eh, em] = parseTime(parts[1]);
    end = new Date(y, m - 1, d, eh, em || 0);
    // Handle crossing midnight (e.g. 19:00 - 03:00)
    if (end <= start) end = new Date(y, m - 1, d + 1, eh, em || 0);
  } else {
    end = new Date(y, m - 1, d, sh + 2, sm || 0); // default 2h
  }
  return { start, end };
};

const escapeICS = (text) =>
  String(text || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const buildVEvent = (event) => {
  const { start, end } = parseEventDateTime(event.date, event.calTime || event.time);
  const uid = `event-${event.id}-${event.date.replace(/\./g, "")}@groemitzereiszeit.de`;
  const dtstamp = fmtICS(new Date()) + "Z";
  return [
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;TZID=Europe/Berlin:${fmtICS(start)}`,
    `DTEND;TZID=Europe/Berlin:${fmtICS(end)}`,
    `SUMMARY:${escapeICS(event.title)}`,
    `DESCRIPTION:${escapeICS(event.description + (event.artist ? `\\nKünstler: ${event.artist}` : ""))}`,
    `LOCATION:${escapeICS(`${event.location} – Grömitzer Eiszeit, Wiese an der Uferstraße, 23743 Grömitz`)}`,
    "END:VEVENT",
  ].join("\r\n");
};

const buildICS = (eventsArr) => {
  const tz = [
    "BEGIN:VTIMEZONE",
    "TZID:Europe/Berlin",
    "BEGIN:STANDARD",
    "DTSTART:19701025T030000",
    "TZOFFSETFROM:+0200",
    "TZOFFSETTO:+0100",
    "TZNAME:CET",
    "RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU",
    "END:STANDARD",
    "BEGIN:DAYLIGHT",
    "DTSTART:19700329T020000",
    "TZOFFSETFROM:+0100",
    "TZOFFSETTO:+0200",
    "TZNAME:CEST",
    "RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU",
    "END:DAYLIGHT",
    "END:VTIMEZONE",
  ].join("\r\n");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Grömitzer Eiszeit//Musik & Meer//DE",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    tz,
    ...eventsArr.map(buildVEvent),
    "END:VCALENDAR",
  ].join("\r\n");
};

const downloadICS = (eventsArr, filename) => {
  const ics = buildICS(eventsArr);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const MusikUndMeer = () => {
  // Events in der Almhüttenzelt
  const [events] = useState([
    {
      id: 1,
      title: "Eröffnung mit dem Lopes Trio",
      date: "27.11.2026",
      time: "17:00 - 21:00 Uhr",
      description: "Live-Musik vom Lopes Trio zur Eröffnung der Grömitzer Eiszeit",
      artist: "Lopes Trio",
      location: "Almhüttenzelt",
      type: "music",
      image: "https://customer-assets.emergentagent.com/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/b7h45ki2_Lopes.jpg",
      imagePosition: "top"
    },
    {
      id: 2,
      title: "DJ Sascha - Partyhits",
      date: "28.11.2026",
      time: "18:00 - 23:00 Uhr",
      description: "DJ Sascha sorgt für beste Stimmung mit Partyhits",
      artist: "DJ Sascha",
      location: "Almhüttenzelt",
      type: "party",
      image: "https://customer-assets-0z36b82j.emergentagent.net/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/etbl0zxw_DJ%20Sascha%20Partyhits.png"
    },
    {
      id: 12,
      title: "90er & 2000er Party mit DJ Andy",
      date: "05.12.2026",
      time: "ab 19:00 Uhr",
      description: "Die größten Hits aus zwei Jahrzehnten. Feiern, tanzen, mitsingen!",
      artist: "DJ Andy",
      location: "Almhüttenzelt",
      type: "party",
      image: "https://customer-assets-0z36b82j.emergentagent.net/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/0vd1gaa6_DJ%20ANDY%2090s%202000s.png"
    },
    {
      id: 3,
      title: "Petterson und Findus",
      date: "06.12.2026",
      time: "15:00 Uhr",
      description: "Die Puppenbühne Hein präsentiert das Weihnachtstheaterstück von Petterson und Findus. Ein besonderes Erlebnis für die ganze Familie zum Nikolaus!",
      artist: "Puppenbühne Hein",
      location: "Almhüttenzelt",
      type: "special",
      image: "https://customer-assets.emergentagent.com/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/4qpf4wn8_Hein.jpg"
    },
    {
      id: 102,
      title: "Kinderdisco",
      date: "06.12.2026",
      time: "16:00 - 18:00 Uhr",
      description: "Nach dem Theater geht's auf die Tanzfläche: Kinderdisco im Almhüttenzelt mit Musik, Lichtshow und Mitmach-Tänzen – Nikolaus-Spaß für alle kleinen Gäste!",
      artist: "Kinderdisco",
      location: "Almhüttenzelt",
      type: "party",
      image: "https://customer-assets-0z36b82j.emergentagent.net/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/a00fdf46f25a0e6a_IMG_2752.webp"
    },
    {
      id: 4,
      title: "Central Dance Academy",
      date: "12.12.2026",
      time: "16:00 Uhr",
      description: "Die Central Dance Academy präsentiert beeindruckende Choreografien aus den Stilrichtungen Hip-Hop und Contemporary, ergänzt durch Showdance und moderne Tanzstile. Die Choreografien zeichnen sich durch Ausdrucksstärke, Kreativität und hohe technische Präzision aus.",
      artist: "Central Dance Academy",
      location: "Almhüttenzelt",
      type: "special",
      image: "https://customer-assets.emergentagent.com/job_487afbd6-c989-4c80-9e03-40b0e7600972/artifacts/i1ek05l6_1772357681.796.jpg"
    },
    {
      id: 13,
      title: "Hüttengaudi",
      date: "12.12.2026",
      time: "ab 19:00 Uhr",
      description: "Party mit DJ – Après-Ski-Hits, Schlager & Malle-Klassiker.",
      artist: "DJ",
      location: "Almhüttenzelt",
      type: "party",
      image: "https://customer-assets-0z36b82j.emergentagent.net/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/jqw46wbp_Hu%CC%88ttengaudi.png"
    },
    {
      id: 14,
      title: "Open Stage Grömitz",
      date: "18.12.2026",
      time: "ab 19:00 Uhr",
      description: "Lokale Talente · Sänger · Musiker · DJs · Comedy · kleine Acts. Bühne frei für Grömitz!",
      artist: "Diverse Künstler",
      location: "Almhüttenzelt",
      type: "special",
      image: "https://customer-assets-0z36b82j.emergentagent.net/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/dos5xj0p_OpenStage%20Gro%CC%88mitz.png"
    },
    {
      id: 5,
      title: "Eiskunstlauf-Show",
      date: "19.12.2026",
      time: "16:00 Uhr",
      description: "Die Eiskunstläufer/innen vom Timmendorfer Eissport Verein zeigen ihr Können auf dem Eis",
      artist: "Timmendorfer Eissport Verein",
      location: "Auf dem Eis",
      type: "special",
      image: "https://customer-assets.emergentagent.com/job_487afbd6-c989-4c80-9e03-40b0e7600972/artifacts/1y1b046e_Eiskunst.jpg",
      imagePosition: "top"
    },
    {
      id: 6,
      title: "Metro-Lounge Live",
      date: "19.12.2026",
      time: "19:00 - 23:00 Uhr",
      description: "Liveband Metro-Lounge - Tanz Party Garantie! Ein unvergesslicher Abend mit großartiger Live-Musik",
      artist: "Metro-Lounge",
      location: "Almhüttenzelt",
      type: "music",
      image: "https://customer-assets.emergentagent.com/job_487afbd6-c989-4c80-9e03-40b0e7600972/artifacts/yi7s56a0_image-jpeg-attachment-1.jpeg"
    },
    {
      id: 7,
      title: "Eiszeit Party Night",
      date: "27.12.2026",
      time: "ab 19:00 Uhr",
      description: "DJ · Party · Dance · House · Party Classics.",
      artist: "DJ",
      location: "Almhüttenzelt",
      type: "party",
      image: "https://customer-assets-0z36b82j.emergentagent.net/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/ij0yav2a_Eiszeit%20Party%20Night1.png"
    },
    {
      id: 8,
      title: "Marty Kessler Live",
      date: "28.12.2026",
      time: "19:00 - 23:00 Uhr",
      description: "One Man Livemusic-Show – Schlager, Fetenhits, Rock & Pop. Das Beste von 1980 bis heute.",
      artist: "Marty Kessler",
      location: "Almhüttenzelt",
      type: "music",
      image: "https://customer-assets.emergentagent.com/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/1uadbtoq_146500.png"
    },
    {
      id: 9,
      title: "Eiszeit Party Night",
      date: "29.12.2026",
      time: "ab 19:00 Uhr",
      description: "DJ · Party · Dance · House · Party Classics.",
      artist: "DJ",
      location: "Almhüttenzelt",
      type: "party",
      image: "https://customer-assets-0z36b82j.emergentagent.net/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/c7gdxaaq_Eiszeit%20Party%20Night2.png"
    },
    {
      id: 10,
      title: "Live Musik",
      date: "30.12.2026",
      time: "19:00 - 23:00 Uhr",
      description: "Live-Musik-Abend im Almhüttenzelt – freu dich auf eine stimmungsvolle Band zum Ausklang des Jahres.",
      artist: "Live Band",
      location: "Almhüttenzelt",
      type: "music",
      image: "https://customer-assets.emergentagent.com/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/16ffk93t_pexels-drum-set-1839383.jpg"
    },
    {
      id: 11,
      title: "DJ Silvester Party",
      date: "31.12.2026",
      time: "18:00 & 21:00 - 02:00 Uhr",
      description: "Die große Silvesterparty mit DJ im Almhüttenzelt – feiert mit uns ins neue Jahr 2027!",
      artist: "DJ",
      location: "Almhüttenzelt",
      type: "special",
      image: "https://images.pexels.com/photos/949592/pexels-photo-949592.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    {
      id: 101,
      title: "Gratis Eislaufen für Grömitzer Kids",
      date: "06.01.2027",
      time: "6–10 J.: 9–13 Uhr · 11–16 J.: 13–18 Uhr",
      calTime: "09:00 - 18:00 Uhr",
      description: "Alle Kinder der Gemeinde Grömitz von 6 bis 16 Jahren laufen kostenlos Schlittschuh – inklusive Leihschlittschuhe, spendiert von der Gemeinde Grömitz. Schnappt euch eure Freunde und kommt vorbei!",
      artist: "Gemeinde Grömitz",
      location: "Eisbahn",
      type: "special",
      image: "https://customer-assets-0z36b82j.emergentagent.net/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/95873a9a31a8c6f6_Eiszeit-8.webp"
    }
  ]);

  const getEventIcon = (type) => {
    switch (type) {
      case "party":
        return <PartyPopper className="w-6 h-6 text-amber-500" />;
      case "music":
        return <Music className="w-6 h-6 text-sky-500" />;
      case "special":
        return <Mic2 className="w-6 h-6 text-rose-500" />;
      default:
        return <Calendar className="w-6 h-6 text-slate-500" />;
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case "party":
        return "from-amber-50 to-orange-50 border-amber-200";
      case "music":
        return "from-sky-50 to-blue-50 border-sky-200";
      case "special":
        return "from-rose-50 to-pink-50 border-rose-200";
      default:
        return "from-slate-50 to-gray-50 border-slate-200";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="relative bg-gradient-to-r from-sky-600 via-sky-500 to-sky-600 py-20 px-6">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzMiAyIDIgNC0yIDQtMiA0LTItMi0yLTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] bg-repeat" />
        </div>
        
        <div className="max-w-4xl mx-auto relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück zur Startseite</span>
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
              <Music className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Playfair Display' }}>
                Musik & Meer
              </h1>
              <p className="text-sky-100 mt-2">Veranstaltungen auf der Grömitzer Eiszeit</p>
            </div>
          </div>
          
          <p className="text-lg text-white/90 max-w-2xl">
            Erlebe unvergessliche Momente auf dem Eis und in unserem Almhüttenzelt! 
            Von Live-Musik bis zu besonderen Events – hier findest du unser aktuelles Programm.
          </p>
        </div>
      </header>

      {/* Events Section */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="mb-12 text-center">
          <span className="text-sm font-medium tracking-widest uppercase text-sky-400 mb-4 block">
            Programm 2026/2027
          </span>
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Unsere Veranstaltungen
          </h2>
          <p className="text-slate-400 mb-6">
            Alle Termine im Überblick – von der Eröffnung bis Silvester
          </p>
          <Button
            onClick={() => downloadICS(events, "Groemitzer-Eiszeit-Programm.ics")}
            className="bg-sky-500 hover:bg-sky-600 rounded-full"
            data-testid="download-all-ics-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Alle Termine in den Kalender
          </Button>
        </div>

        {/* Events List */}
        <div className="space-y-6">
          {events.map((event) => (
            <Card 
              key={event.id} 
              className={`border-0 overflow-hidden bg-gradient-to-r ${getEventColor(event.type)} shadow-lg hover:shadow-xl transition-shadow`}
              data-testid={`event-${event.id}`}
            >
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row">
                  {/* Event Image */}
                  {event.image && (
                    <div className="md:w-64 h-48 md:h-auto flex-shrink-0">
                      <img 
                        src={event.image} 
                        alt={event.title}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: event.imagePosition || "center" }}
                      />
                    </div>
                  )}
                  
                  {/* Event Info */}
                  <div className="p-6 flex flex-col md:flex-row md:items-center gap-6 flex-1">
                    {/* Date Badge */}
                    <div className="flex-shrink-0 text-center bg-white rounded-xl p-4 shadow-sm w-24">
                      <div className="text-2xl font-bold text-slate-900">
                        {event.date.split('.')[0]}
                      </div>
                      <div className="text-sm text-slate-500">
                        {event.date.split('.')[1]}.{event.date.split('.')[2]}
                      </div>
                    </div>
                    
                    {/* Event Details */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getEventIcon(event.type)}
                        <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
                          {event.title}
                        </h3>
                      </div>
                      <p className="text-slate-600 mb-3">{event.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {event.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mic2 className="w-4 h-4" />
                          {event.artist}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </span>
                      </div>
                      <button
                        onClick={() =>
                          downloadICS(
                            [event],
                            `Eiszeit-${event.date.replace(/\./g, "-")}-${event.title.replace(/[^a-z0-9]/gi, "-")}.ics`
                          )
                        }
                        className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-sky-700 hover:text-sky-900 hover:bg-white/60 rounded-full px-3 py-1.5 transition-colors"
                        data-testid={`save-calendar-${event.id}`}
                      >
                        <CalendarPlus className="w-4 h-4" />
                        Im Kalender speichern
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info Box */}
        <div className="mt-12 bg-slate-800/50 backdrop-blur rounded-2xl p-8 border border-slate-700">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
              <Snowflake className="w-6 h-6 text-sky-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Playfair Display' }}>
                Weitere Events folgen!
              </h3>
              <p className="text-slate-400">
                Wir planen noch weitere Veranstaltungen. Folge uns auf Social Media oder schau regelmäßig 
                hier vorbei, um nichts zu verpassen!
              </p>
              <p className="text-slate-400 mt-4">
                <strong className="text-sky-400">Tipp:</strong> Für größere Gruppen oder Firmenevents kannst du 
                uns gerne direkt kontaktieren.
              </p>
              <Link to="/#contact">
                <Button className="mt-4 bg-sky-500 hover:bg-sky-600">
                  Kontakt aufnehmen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 py-8 px-6 border-t border-slate-800">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Snowflake className="w-6 h-6 text-sky-400" />
            <span className="text-xl font-semibold text-white" style={{ fontFamily: 'Playfair Display' }}>
              Grömitzer Eiszeit
            </span>
          </div>
          <p className="text-slate-500 text-sm">
            27.11.2026 - 06.01.2027 | Wiese an der Uferstraße, 23743 Grömitz
          </p>
          <div className="flex justify-center gap-6 mt-4 text-sm text-slate-500">
            <Link to="/" className="hover:text-sky-400 transition-colors">Startseite</Link>
            <Link to="/impressum" className="hover:text-sky-400 transition-colors">Impressum</Link>
            <Link to="/agb" className="hover:text-sky-400 transition-colors">AGB</Link>
            <Link to="/datenschutz" className="hover:text-sky-400 transition-colors">Datenschutz</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MusikUndMeer;
